import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useCart } from '../contexts/CartContext';
import { toImageSrc, toThumbSrc } from '../services/api';

const CartWrapper = styled.div`
  padding: var(--spacing-8) 0;
  min-height: 60vh;
`;

const Title = styled.h1`
  font-size: var(--font-size-3xl);
  font-weight: 600;
  margin-bottom: var(--spacing-8);
  color: var(--color-secondary);
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: var(--spacing-16);
  
  .icon {
    font-size: 4rem;
    margin-bottom: var(--spacing-4);
  }
  
  h2 {
    color: var(--color-secondary);
    margin-bottom: var(--spacing-4);
  }
  
  p {
    color: var(--color-text-light);
    margin-bottom: var(--spacing-6);
  }
`;

const CartContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--spacing-8);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-4);
  }
`;

const CartItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
`;

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr auto auto auto;
  gap: var(--spacing-4);
  align-items: center;
  padding: var(--spacing-4);
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
`;

const ItemImage = styled.div`
  width: 80px;
  height: 80px;
  background-color: #f9fafb;
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  overflow: hidden;
  padding: var(--spacing-2);
  box-sizing: border-box;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  
  span {
    font-size: 2rem;
  }
`;

const ItemInfo = styled.div`
  .name {
    font-weight: 600;
    color: var(--color-secondary);
    margin-bottom: var(--spacing-1);
  }
  
  .sku {
    font-size: var(--font-size-sm);
    color: var(--color-text-light);
    margin-bottom: var(--spacing-1);
  }
  
  .category {
    font-size: var(--font-size-xs);
    color: var(--color-text-light);
  }
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  
  button {
    background: none;
    border: none;
    padding: var(--spacing-2) var(--spacing-3);
    cursor: pointer;
    font-size: var(--font-size-lg);
    
    &:hover {
      background-color: var(--color-primary);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  
  input {
    border: none;
    text-align: center;
    width: 50px;
    padding: var(--spacing-2);
    font-weight: 500;
  }
`;

const ItemPrice = styled.div`
  font-weight: 600;
  color: var(--color-secondary);
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: var(--color-error);
  cursor: pointer;
  padding: var(--spacing-2);
  border-radius: var(--border-radius);
  
  &:hover {
    background: #fee2e2;
  }
`;

const CartSummary = styled.div`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-6);
  height: fit-content;
  
  h3 {
    color: var(--color-secondary);
    margin-bottom: var(--spacing-4);
  }
`;

const SummaryRow = styled.div<{ isTotal?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-2) 0;
  
  ${({ isTotal }) => isTotal && `
    border-top: 1px solid var(--color-border);
    margin-top: var(--spacing-4);
    padding-top: var(--spacing-4);
    font-weight: 600;
    font-size: var(--font-size-lg);
    color: var(--color-secondary);
  `}
`;

const CheckoutButton = styled(Link)`
  display: block;
  background-color: var(--color-secondary);
  color: white;
  padding: var(--spacing-4) var(--spacing-6);
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-lg);
  font-weight: 600;
  text-decoration: none;
  text-align: center;
  margin-top: var(--spacing-4);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #1f1e3a;
  }
`;

const ClearCartButton = styled.button`
  background: none;
  border: 1px solid var(--color-border);
  color: var(--color-text-light);
  padding: var(--spacing-2) var(--spacing-4);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  cursor: pointer;
  margin-top: var(--spacing-4);
  
  &:hover {
    background: #f9fafb;
    color: var(--color-text);
  }
`;

const Cart: React.FC = () => {
  const { items, totalItems, updateQuantity, removeItem, clearCart, cartCalculation, isCalculating } = useCart();

  const getProductEmoji = (categories?: string[]) => {
    if (!categories || categories.length === 0) return '🏺';
    if (categories.some(cat => cat.toLowerCase().includes('ljus'))) return '🕯️';
    if (categories.some(cat => cat.toLowerCase().includes('godis'))) return '🍬';
    if (categories.some(cat => cat.toLowerCase().includes('cottage'))) return '☕';
    return '🏺';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(amount);
  };

  const handleQuantityChange = (itemId: number, newQuantity: number) => {
    if (newQuantity >= 1) {
      updateQuantity(itemId, newQuantity);
    }
  };

  if (items.length === 0) {
    return (
      <CartWrapper>
        <div className="container">
          <Title>Kundvagn</Title>
          
          <EmptyCart>
            <div className="icon">🛒</div>
            <h2>Din kundvagn är tom</h2>
            <p>Lägg till några produkter från vår butik för att komma igång!</p>
            <Link to="/produkter" className="btn btn-primary">
              Börja handla
            </Link>
          </EmptyCart>
        </div>
      </CartWrapper>
    );
  }

  return (
    <CartWrapper>
      <div className="container">
        <Title>Kundvagn ({totalItems} {totalItems === 1 ? 'produkt' : 'produkter'})</Title>
        
        <CartContent>
          <CartItems>
            {items.map((item) => (
              <CartItem key={item.id}>
                <ItemImage>
                  {item.images && item.images.length > 0 ? (
                    <img
                      loading="lazy"
                      src={toThumbSrc(item.images[0])}
                      alt={item.name}
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        target.onerror = null;
                        target.src = toImageSrc(item.images![0]);
                      }}
                    />
                  ) : (
                    <span>{getProductEmoji(item.categories)}</span>
                  )}
                </ItemImage>
                
                <ItemInfo>
                  <div className="name">
                    {item.name}
                    {item.has_discount && (
                      <span style={{
                        backgroundColor: '#dc2626',
                        color: 'white',
                        fontSize: '0.7em',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        marginLeft: '8px',
                        fontWeight: '600'
                      }}>
                        RABATT
                      </span>
                    )}
                  </div>
                  <div className="sku">Art.nr: {item.sku}</div>
                  <div className="category">{item.categories?.join(', ') || ''}</div>
                  {item.has_discount && (
                    <div className="price-info" style={{ marginTop: '4px' }}>
                      <span style={{
                        textDecoration: 'line-through',
                        color: '#9ca3af',
                        fontSize: '0.85em',
                        marginRight: '6px'
                      }}>
                        {formatCurrency(item.original_price)}
                      </span>
                      <span style={{ color: '#dc2626', fontWeight: '600', fontSize: '0.9em' }}>
                        {formatCurrency(item.final_price)} st
                      </span>
                    </div>
                  )}
                </ItemInfo>
                
                <QuantityControls>
                  <button 
                    onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                  >
                    −
                  </button>
                  <input 
                    type="number" 
                    value={item.quantity} 
                    onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value) || 1)}
                    min="1"
                  />
                  <button 
                    onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                  >
                    +
                  </button>
                </QuantityControls>
                
                <ItemPrice>
                  {formatCurrency(item.item_total)}
                </ItemPrice>
                
                <RemoveButton
                  onClick={() => removeItem(item.id)}
                  title="Ta bort från kundvagn"
                >
                  🗑️
                </RemoveButton>
              </CartItem>
            ))}
            
            <ClearCartButton onClick={clearCart}>
              Töm kundvagn
            </ClearCartButton>
          </CartItems>
          
          <CartSummary>
            <h3>Ordersammanfattning</h3>
            
            <SummaryRow>
              <span>Delsumma ({totalItems} produkter):</span>
              <span>
                {isCalculating ? 'Beräknar...' : formatCurrency(cartCalculation?.subtotal || 0)}
              </span>
            </SummaryRow>
            
            {cartCalculation?.campaign_discount_amount && cartCalculation.campaign_discount_amount > 0 && (
              <SummaryRow>
                <span style={{ color: '#16a34a' }}>
                  Rabattkod ({cartCalculation.campaign_discount}%):
                </span>
                <span style={{ color: '#16a34a' }}>-{formatCurrency(cartCalculation.campaign_discount_amount)}</span>
              </SummaryRow>
            )}
            
            <SummaryRow>
              <span>Frakt:</span>
              <span>
                {isCalculating ? 'Beräknar...' : (
                  cartCalculation?.shipping_cost === 0 ? 'Gratis' : formatCurrency(cartCalculation?.shipping_cost || 49)
                )}
              </span>
            </SummaryRow>
            
            <SummaryRow>
              <span>Moms (25%):</span>
              <span>
                {isCalculating ? 'Beräknar...' : formatCurrency(cartCalculation?.tax_amount || 0)}
              </span>
            </SummaryRow>
            
            <SummaryRow isTotal>
              <span>Totalt:</span>
              <span>
                {isCalculating ? 'Beräknar...' : formatCurrency(cartCalculation?.total_amount || 0)}
              </span>
            </SummaryRow>
            
            <CheckoutButton to="/kassa">
              Fortsätt till kassan
            </CheckoutButton>
            
            <Link to="/produkter" style={{ 
              display: 'block', 
              textAlign: 'center', 
              marginTop: 'var(--spacing-4)',
              color: 'var(--color-secondary)',
              textDecoration: 'none',
              fontSize: 'var(--font-size-sm)'
            }}>
              ← Fortsätt handla
            </Link>
          </CartSummary>
        </CartContent>
      </div>
    </CartWrapper>
  );
};

export default Cart;
